import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Paper, CircularProgress } from '@mui/material';
import {
  formatCalendarDate,
  formatCalendarDateInZone,
  formatTimeWithZone,
  getTimeInMinutesInZone,
} from '../../../utils/timezone';
import { fetchSlots, fetchAvailability } from '../services/appointmentService';
import type { TimeSlot } from '../services/appointmentService';
import { CONSULTATION_SERVICE_ID } from '../../../config/api';
import type { AppointmentTaker } from '../../marketplace/types';
import { BOOKING_THEME } from '../bookingTheme';

interface TimeSlotPickerProps {
  date: Date;
  taker: AppointmentTaker;
  onSlotSelect: (slot: TimeSlot) => void;
  selectedSlot?: TimeSlot | null;
  /** Filter out slots starting before this time */
  minTime?: Date;
  emptyMessage?: string;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  date,
  taker,
  onSlotSelect,
  selectedSlot,
  minTime,
  emptyMessage,
}) => {
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tz = taker.timeZone || 'America/Toronto';

  useEffect(() => {
    if (!date) return;
    loadSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date.toDateString(), minTime?.getTime(), taker.userId]);

  const loadSlots = async () => {
    setLoading(true);
    setError(null);

    try {
      const dateStr = formatCalendarDate(date);

      const [slotsResult, availResult] = await Promise.all([
        fetchSlots({
          date,
          serviceId: CONSULTATION_SERVICE_ID,
          appointmentForUserId: taker.userId,
          appointmentForEmail: taker.email,
        }),
        fetchAvailability({ startDate: dateStr, endDate: dateStr, appointmentForEmail: taker.email }),
      ]);

      const blockedRecords = (availResult.availability ?? []).filter(
        (rec: Record<string, unknown>) =>
          rec.isAvailable === false && rec.appointmentOwner === taker.email
      );

      const timeStringToMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
      };

      const slotOverlaps = (start: Date, end: Date, block: Record<string, unknown>) => {
        if (!block.startTime || !block.endTime || block.startTime === '' || block.endTime === '') return true;
        const s = getTimeInMinutesInZone(start, tz);
        const e = getTimeInMinutesInZone(end, tz);
        const bs = timeStringToMinutes(block.startTime as string);
        const be = timeStringToMinutes(block.endTime as string);
        return s < be && e > bs;
      };

      const normalized = slotsResult
        .map((slot) => {
          if (minTime && slot.start < minTime) return null;

          let available = slot.available;
          if (available && blockedRecords.length > 0) {
            const sy = slot.start.getUTCFullYear();
            const sm = slot.start.getUTCMonth();
            const sd = slot.start.getUTCDate();

            available = !blockedRecords.some((block: Record<string, unknown>) => {
              const bd = new Date(block.date as string);
              const sameDay =
                sy === bd.getUTCFullYear() && sm === bd.getUTCMonth() && sd === bd.getUTCDate();
              return sameDay && slotOverlaps(slot.start, slot.end, block);
            });
          }

          return { ...slot, available };
        })
        .filter((s): s is TimeSlot => s !== null);

      setTimeSlots(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load slots');
      setTimeSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (d: Date) => formatTimeWithZone(d, 'h:mm a', tz);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} sx={{ color: '#3c3c3c' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: BOOKING_THEME.textSecondary, mb: 1.5 }}>
        Available slots for {formatCalendarDateInZone(date, 'EEEE, MMM d', tz)}
      </Typography>

      {error && (
        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: BOOKING_THEME.promoAccordionBg, border: `1px solid ${BOOKING_THEME.border}` }}>
          <Typography color={BOOKING_THEME.textSecondary} variant="body2">{error}</Typography>
        </Paper>
      )}

      {!error && timeSlots.length === 0 && (
        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: BOOKING_THEME.promoAccordionBg, border: `1px solid ${BOOKING_THEME.border}` }}>
          <Typography color={BOOKING_THEME.textSecondary} variant="body2">
            {emptyMessage || 'No available slots for this date — try another date.'}
          </Typography>
        </Paper>
      )}

      {!error && timeSlots.filter((s) => s.available).length === 0 && timeSlots.length > 0 && (
        <Paper sx={{ p: 2, textAlign: 'center', bgcolor: BOOKING_THEME.promoAccordionBg, border: `1px solid ${BOOKING_THEME.border}` }}>
          <Typography color={BOOKING_THEME.textSecondary} variant="body2">
            All slots are blocked for this date — try another date.
          </Typography>
        </Paper>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {timeSlots.map((slot, idx) => {
          const isSelected =
            selectedSlot &&
            selectedSlot.start.getTime() === slot.start.getTime() &&
            selectedSlot.end.getTime() === slot.end.getTime();

          return (
            <Button
              key={`${slot.start.toISOString()}-${idx}`}
              variant="outlined"
              onClick={() => slot.available && onSlotSelect(slot)}
              disabled={!slot.available}
              sx={{
                flex: '1 1 calc(50% - 8px)',
                minWidth: '120px',
                maxWidth: '160px',
                minHeight: '44px',
                fontSize: '0.8rem',
                borderColor: isSelected ? BOOKING_THEME.slotBorder : slot.available ? BOOKING_THEME.border : BOOKING_THEME.border,
                borderWidth: isSelected ? 2 : 1,
                color: isSelected ? BOOKING_THEME.textPrimary : slot.available ? BOOKING_THEME.textPrimary : BOOKING_THEME.textSecondary,
                backgroundColor: isSelected ? BOOKING_THEME.slotSelected : slot.available ? BOOKING_THEME.cardBg : 'rgba(0,0,0,0.03)',
                opacity: slot.available ? 1 : 0.6,
                cursor: slot.available ? 'pointer' : 'not-allowed',
                '&:hover': slot.available
                  ? { backgroundColor: 'rgba(216, 6, 33, 0.08)', borderColor: BOOKING_THEME.slotBorder }
                  : {},
                '&.Mui-disabled': { borderColor: BOOKING_THEME.border, color: BOOKING_THEME.textSecondary, opacity: 0.4 },
              }}
            >
              {formatTime(slot.start)} – {formatTime(slot.end)}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default TimeSlotPicker;
